import { useState } from "react";
import { Link } from "react-router-dom";
import { PackageSearch, Check, MapPin, CreditCard, Package } from "lucide-react";
import { motion } from "framer-motion";
import { api, inr, fmtDate, errMsg } from "../lib/api";
import { statusChip, statusLabel } from "./OrdersPage";

export default function TrackOrderPage() {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.post("/orders/track", { order_id: orderId.trim(), email: email.trim() });
      setOrder(data);
    } catch (err) {
      setError(errMsg(err, "We couldn't find an order with those details. Please check your Order ID and email address."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 lg:py-16" data-testid="track-order-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">Order Support</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 mt-3 tracking-tight">Track Your Order</h1>
      <p className="text-stone-500 mt-2">Enter the Order ID from your confirmation and the email you ordered with.</p>

      <form onSubmit={submit} className="mt-8 p-6 rounded-2xl bg-white border border-[#E9E2D7] grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3" data-testid="track-order-form">
        <input required value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Order ID (e.g. CGO1A2B3C4)" className="h-12 px-4 rounded-xl border border-stone-200 text-sm outline-none focus:border-[#B45309] font-mono uppercase" data-testid="track-order-id" />
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className="h-12 px-4 rounded-xl border border-stone-200 text-sm outline-none focus:border-[#B45309]" data-testid="track-order-email" />
        <button disabled={busy} className="h-12 px-7 rounded-xl bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors disabled:opacity-60" data-testid="track-order-submit">
          {busy ? "Checking…" : "Track"}
        </button>
      </form>

      {error && (
        <div className="mt-6 p-5 rounded-2xl bg-red-50 border border-red-100 text-sm text-red-700" data-testid="track-order-error">{error}</div>
      )}

      {order && <TrackResult order={order} />}

      {!order && !error && (
        <div className="mt-10 text-center py-10 rounded-3xl bg-[#F9F3EA] border border-[#E9E2D7]">
          <PackageSearch className="mx-auto text-[#B45309]" size={30} />
          <p className="text-sm text-stone-500 mt-3 max-w-sm mx-auto">Signed in? You can also see every order in <Link to="/orders" className="font-bold text-[#B45309] hover:underline" data-testid="track-my-orders-link">My Orders</Link>.</p>
        </div>
      )}
    </div>
  );
}

function TrackResult({ order }) {
  const currentIdx = order.status_steps.findIndex((s) => s.key === order.status);
  const a = order.address;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="track-order-result">
      <div className="p-6 rounded-2xl bg-white border border-[#E9E2D7]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono font-bold text-stone-900" data-testid="track-result-id">{order.order_id}</p>
            <p className="text-xs text-stone-400 mt-0.5">Placed on {fmtDate(order.created_at)} · ETA {fmtDate(order.estimated_delivery)}</p>
          </div>
          <span className={`px-4 py-1.5 rounded-full text-sm font-bold ${statusChip(order.status)}`} data-testid="track-result-status">{statusLabel(order.status)}</span>
        </div>

        <div className="flex items-start mt-8" data-testid="track-timeline">
          {order.status_steps.map((s, i) => {
            const done = i <= currentIdx;
            return (
              <div key={s.key} className="flex-1 relative" data-testid={`track-step-${s.key}`}>
                {i > 0 && <div className={`absolute top-4 -left-1/2 w-full h-0.5 ${done ? "bg-[#78866B]" : "bg-stone-200"}`} />}
                <div className="relative flex flex-col items-center text-center">
                  <span className={`w-8 h-8 rounded-full grid place-items-center border-2 ${done ? "bg-[#78866B] border-[#78866B] text-white" : "bg-white border-stone-200 text-stone-300"}`}>
                    <Check size={14} />
                  </span>
                  <p className={`text-[10px] sm:text-xs font-semibold mt-2 ${done ? "text-stone-800" : "text-stone-400"}`}>{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div className="p-5 rounded-2xl bg-white border border-[#E9E2D7]">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-400 flex items-center gap-2"><Package size={13} /> Order summary</p>
          <div className="mt-3 space-y-2.5">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                {i.image && <img src={i.image} alt={i.name} className="w-10 h-10 rounded-lg object-cover" />}
                <span className="flex-1 text-stone-700 line-clamp-1">{i.name} × {i.qty}</span>
                <span className="font-semibold text-stone-900">{inr(i.unit_price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-100 mt-3 pt-3 flex justify-between font-bold text-stone-900 text-sm"><span>Total</span><span>{inr(order.total)}</span></div>
        </div>
        <div className="p-5 rounded-2xl bg-white border border-[#E9E2D7] space-y-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-400 flex items-center gap-2"><MapPin size={13} /> Delivering to</p>
            <p className="text-sm font-semibold text-stone-800 mt-2">{a.full_name}</p>
            <p className="text-sm text-stone-500">{a.city}, {a.state} — {a.pincode}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-400 flex items-center gap-2"><CreditCard size={13} /> Payment</p>
            <p className="text-sm font-semibold text-stone-800 mt-2">{order.payment_method.toUpperCase()} · {order.payment_status === "paid" ? "Paid" : "Pay on delivery"}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
